import { useEffect, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import {
  auDateTime,
  canFileInvoice,
  hours,
  isDecidedInvoice,
  type ContractorInvoiceDetail,
  type CorrectableField,
  type FieldSource,
} from "@contractorhub/shared";
import { ApiRequestError, apiDownload, apiFetch, errorMessage } from "../../../lib/api";
import { allowedActions } from "../../../lib/invoice-review";
import {
  adminInvoiceDetailQuery,
  adminInvoicePdfQuery,
  queryKeys,
} from "../../../lib/queries";
import { ExtractedFieldsCard } from "../../../components/invoices/extracted-fields-card";
import { InvoiceActionBar } from "../../../components/invoices/invoice-action-bar";
import {
  ConfidencePill,
  InvoiceChecksBadges,
  InvoiceStatusBadge,
  ValidationBadge,
  reviewedText,
} from "../../../components/invoices/invoice-badges";
import { InvoiceExplanationCard } from "../../../components/invoices/invoice-explanation-card";
import { InvoiceHistory } from "../../../components/invoices/invoice-history";
import { InvoicePaymentsCard } from "../../../components/invoices/invoice-payments-card";
import { InvoiceProjectCard } from "../../../components/invoices/invoice-project-card";
import { LineProjectsCard } from "../../../components/invoices/line-projects-card";
import { InvoicePdfCard } from "../../../components/invoices/invoice-pdf-card";
import type { PdfHighlight } from "../../../components/invoices/pdf-pages";
import { KeyFigures } from "../../../components/invoices/key-figures";
import { MonthWeeksCard } from "../../../components/invoices/month-weeks-card";
import { TimesheetComparisonCard } from "../../../components/invoices/timesheet-comparison-card";
import { VerdictSummary } from "../../../components/invoices/verdict-summary";
import { useToast } from "../../../components/toast";
import { ListBackLink, ListPager } from "../../../components/list-pager";
import { Alert, Avatar, Tabs, Z, cn } from "../../../components/ui";

type ReviewTab = "fields" | "checks" | "projects" | "weeks" | "history";

/** How long the page keeps asking whether the read has finished, then offers Re-run. */
const POLL_FOR_MS = 3 * 60_000;
const POLL_EVERY_MS = 3_000;
/** After the usual wait, how often to look for the read finishing — or being called stuck. */
const POLL_SLOW_MS = 60_000;

export const Route = createFileRoute("/_authed/_admin/invoices_/$id")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(adminInvoiceDetailQuery(params.id)),
  // A fresh page for each invoice: the pager steps between ids on this one
  // route, and the fields card is seeded once — kept, it would carry one
  // invoice's typed fields onto the next.
  remountDeps: ({ params }) => params.id,
  component: InvoiceDetailPage,
});

/**
 * One invoice: the PDF beside what was read from it, the comparison against the
 * approved timesheets, the weeks of the month, and the decision buttons.
 *
 * While the document is being read the page polls, bounded: three minutes, then
 * it stops and offers Re-run. When the read lands, the inbox is invalidated so
 * its validation column and counts catch up.
 */
function InvoiceDetailPage() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  // Which read we are waiting on, when it started, and whether we have stopped
  // waiting for it. Kept in refs and read only inside the interval callback,
  // which runs after each fetch — never during render, which must stay pure.
  const pollStartedAt = useRef<number | null>(null);
  const pollRead = useRef<string | null>(null);
  const [pollExpired, setPollExpired] = useState(false);
  // Unsaved edits in the fields card, so Approve cannot decide on stale fields.
  const [fieldsDirty, setFieldsDirty] = useState(false);

  const base = adminInvoiceDetailQuery(id);
  const { data: initial } = useSuspenseQuery(base);

  const { data: live } = useQuery({
    ...base,
    refetchInterval: (query) => {
      const current = query.state.data;
      if (!current) return false;
      if (current.extraction.status !== "PENDING") {
        pollRead.current = null;
        if (pollStartedAt.current !== null) {
          // The read finished while we watched: the inbox row moved too.
          pollStartedAt.current = null;
          void queryClient.invalidateQueries({ queryKey: ["admin", "invoices"] });
        }
        return false;
      }
      // A different read than the one we gave up on — the contractor replaced
      // the file, another admin re-ran it, the worker picked it up — gets a
      // fresh three minutes. `attempts` restarts at 0 for every new read.
      const read = `${current.fileVersion}:${current.extraction.attempts}`;
      if (pollRead.current !== read) {
        pollRead.current = read;
        pollStartedAt.current = null;
        setPollExpired(false);
      }
      pollStartedAt.current ??= Date.now();
      // Past the usual wait, keep looking — slowly — so the page learns when the
      // server calls the read stuck and a re-run becomes possible.
      if (Date.now() - pollStartedAt.current > POLL_FOR_MS) {
        setPollExpired(true);
        return POLL_SLOW_MS;
      }
      return POLL_EVERY_MS;
    },
    refetchIntervalInBackground: false,
  });
  const detail: ContractorInvoiceDetail = live ?? initial;
  const pending = detail.extraction.status === "PENDING";

  const setDetail = (next: ContractorInvoiceDetail) =>
    queryClient.setQueryData(queryKeys.adminInvoiceDetail(id), next);

  const rerun = useMutation({
    mutationFn: () =>
      apiFetch("POST /api/manage/invoices/{id}/rerun-extraction", {
        params: { id },
        body: { version: detail.version },
      }),
    onSuccess: ({ queued, invoice }) => {
      setDetail(invoice);
      pollStartedAt.current = queued ? Date.now() : null;
      pollRead.current = queued
        ? `${invoice.fileVersion}:${invoice.extraction.attempts}`
        : null;
      setPollExpired(false);
      toast(
        queued
          ? "Reading the document again…"
          : "The read could not be queued — enter the fields by hand.",
        {
          tone: queued ? "success" : "error",
        }
      );
    },
    onError: async (err) => {
      if (err instanceof ApiRequestError && err.code === "STALE_WRITE") {
        await queryClient.invalidateQueries({
          queryKey: queryKeys.adminInvoiceDetail(id),
        });
      }
      toast(errorMessage(err), { tone: "error" });
    },
  });

  const actions = allowedActions(detail.status, detail.extraction.status);
  const editable = detail.status === "SUBMITTED" || detail.status === "CHANGES_REQUESTED";
  const decided = isDecidedInvoice(detail.status);
  const canRerun =
    actions.has("rerun") ||
    (detail.extraction.status === "PENDING" && detail.extraction.stuck);

  // CINV-30: a field's quote, found and marked on the PDF beside the form.
  const [highlight, setHighlight] = useState<PdfHighlight | null>(null);
  const pdfRef = useRef<HTMLDivElement>(null);
  const showSource = (_field: CorrectableField, source: FieldSource) => {
    setHighlight({ quotes: source.quotes, key: Date.now() });
    // Side by side on a wide screen; below the fields on a narrow one.
    if (!window.matchMedia("(min-width: 1024px)").matches)
      pdfRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const onHighlight = (found: boolean) => {
    if (!found && highlight)
      toast(`Couldn't find it in the PDF's text — the model read it from: “${highlight.quotes[0]?.text ?? ""}”`, {
        tone: "default",
      });
  };

  const [reviewTab, setReviewTab] = useState<ReviewTab>("fields");
  const tabsRef = useRef<HTMLDivElement>(null);
  const showAllChecks = () => {
    setReviewTab("checks");
    tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const showLines = () => {
    setReviewTab("projects");
    tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // The header pins under the app bar, and the PDF pins under the header — so the
  // PDF needs the header's height, which changes as the title and actions wrap.
  const headerRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setHeaderHeight(Math.ceil(entry.borderBoxSize[0]?.blockSize ?? 0));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const checks = detail.validation?.checks ?? [];
  const issues = checks.filter((c) => c.result === "MISMATCH" || c.result === "REVIEW");
  const engagedWeeks = detail.validation?.weeks.filter((w) => w.engaged) ?? [];

  return (
    <div style={{ "--invoice-header": `${headerHeight}px` } as React.CSSProperties}>
      <header
        ref={headerRef}
        className={cn(
          "-mx-4 -mt-5 mb-5 border-b border-line bg-canvas/90 px-4 pb-4 pt-5 backdrop-blur sm:-mx-6 sm:-mt-6 sm:px-6 sm:pt-6 lg:sticky lg:top-16 lg:-mx-8 lg:px-8",
          Z.sticky
        )}
      >
        <nav
          aria-label="Breadcrumb"
          className="mb-3 flex items-center gap-1.5 text-xs text-subtle"
        >
          <ListBackLink list="invoices" className="hover:text-text">
            Invoices
          </ListBackLink>
          <ChevronRight className="h-3 w-3" aria-hidden="true" />
          <span className="truncate font-medium text-text">
            {detail.invoiceNumber ?? "Invoice"} · {detail.contractorName}
          </span>
          <ListPager list="invoices" id={detail.id} className="ml-auto" />
        </nav>
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
          <div className="flex min-w-0 items-center gap-3">
            <Avatar name={detail.contractorName} size="lg" />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <h1 className="truncate text-xl font-semibold tracking-tight text-text">
                  {detail.invoiceNumber ?? `Invoice for ${detail.periodLabel}`}
                </h1>
                <span className="flex flex-wrap items-center gap-1.5">
                  <InvoiceStatusBadge status={detail.status} />
                  {decided ? (
                    // CINV-34: approving was the review; the checks below are today's.
                    <InvoiceChecksBadges row={detail} />
                  ) : (
                    <>
                      <ValidationBadge
                        status={detail.validationStatus}
                        extractionStatus={detail.extraction.status}
                      />
                      <ConfidencePill value={detail.confidence} />
                    </>
                  )}
                </span>
              </div>
              <p className="mt-0.5 truncate text-sm text-subtle">
                {detail.contractorName}
                {detail.contractorCompany ? ` · ${detail.contractorCompany}` : ""} ·{" "}
                {detail.periodLabel} · received {auDateTime(new Date(detail.submittedAt))}
              </p>
            </div>
          </div>
          <InvoiceActionBar
            detail={detail}
            fieldsDirty={editable && fieldsDirty}
            onChanged={setDetail}
          />
        </div>
      </header>

      {detail.reviewNote &&
        (detail.status === "CHANGES_REQUESTED" || detail.status === "REJECTED") && (
          <Alert
            tone="warn"
            className="mb-5"
            title={detail.status === "REJECTED" ? "Rejected" : "Correction requested"}
          >
            {detail.reviewNote}
            {detail.reviewerName ? (
              <span className="text-xs"> — {detail.reviewerName}</span>
            ) : null}
          </Alert>
        )}

      {detail.changedSinceApproval && (
        // CINV-28: what this invoice was approved against has since moved.
        <Alert tone="error" className="mb-5" title="Changed since it was approved">
          Approved hours for {detail.periodLabel} were{" "}
          {hours(detail.changedSinceApproval.approvedHoursAtApproval)} when this invoice was
          approved, and are {hours(detail.approvedHours ?? 0)} now — a week may have been
          returned or un-approved. See the Weeks tab.
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
        <div ref={pdfRef} className="order-last scroll-mt-20 lg:order-first">
          <div className="lg:sticky lg:top-[calc(4rem+var(--invoice-header)+1.25rem)]">
            <InvoicePdfCard
              query={adminInvoicePdfQuery(id, detail.fileVersion)}
              fileName={detail.fileName}
              fileVersion={detail.fileVersion}
              frameClassName="h-[70vh] lg:h-[calc(100dvh-4rem-var(--invoice-header)-6.5rem)] lg:min-h-96"
              highlight={highlight}
              onHighlight={onHighlight}
              onDownload={() =>
                apiDownload(
                  "GET /api/manage/invoices/{id}/file",
                  { params: { id } },
                  detail.fileName
                )
              }
            />
          </div>
        </div>

        <div className="min-w-0 space-y-5">
          <KeyFigures detail={detail} />
          <InvoiceProjectCard
            detail={detail}
            fieldsDirty={editable && fieldsDirty}
            onSaved={setDetail}
            onShowLines={showLines}
          />
          {decided && <InvoicePaymentsCard detail={detail} onChanged={setDetail} />}
          {decided && (
            <p className="text-sm text-muted">
              <span className="font-medium text-text">Reviewed.</span>{" "}
              {reviewedText(detail).replace(/\.?$/, ".")} The checks below are as they stand today.
            </p>
          )}
          <VerdictSummary
            detail={detail}
            polling={pending && !pollExpired}
            pollExpired={pollExpired}
            canRerun={canRerun}
            rerunPending={rerun.isPending}
            onRerun={() => rerun.mutate()}
            onShowAllChecks={showAllChecks}
          />
          <InvoiceExplanationCard detail={detail} onChanged={setDetail} />

          <div
            ref={tabsRef}
            className="scroll-mt-[calc(4rem+var(--invoice-header)+1rem)]"
          >
            <Tabs
              className="mb-4"
              value={reviewTab}
              onChange={setReviewTab}
              items={[
                { value: "fields", label: "Fields" },
                {
                  value: "checks",
                  label: "All checks",
                  count: issues.length || undefined,
                },
                {
                  value: "projects",
                  label: "Projects",
                  count: detail.untaggedLineCount || undefined,
                },
                {
                  value: "weeks",
                  label: "Weeks",
                  count: engagedWeeks.length || undefined,
                },
                { value: "history", label: "History", count: detail.events.length },
              ]}
            />
            {/* Kept mounted while hidden: the fields card holds unsaved edits,
                and switching tabs must not throw them away. */}
            <div hidden={reviewTab !== "fields"}>
              <ExtractedFieldsCard
                detail={detail}
                save={(body) =>
                  apiFetch("PATCH /api/manage/invoices/{id}/fields", { params: { id: detail.id }, body })
                }
                detailQueryKey={queryKeys.adminInvoiceDetail(detail.id)}
                editable={editable}
                canRerun={actions.has("rerun")}
                rerunPending={rerun.isPending}
                onRerun={() => rerun.mutate()}
                onSaved={setDetail}
                onDirtyChange={setFieldsDirty}
                sources={detail.sources}
                onShowSource={showSource}
                suggestions={detail.fieldSuggestions}
              />
            </div>
            {reviewTab === "checks" && (
              <TimesheetComparisonCard
                validation={detail.validation}
                periodLabel={detail.periodLabel}
              />
            )}
            {reviewTab === "projects" && (
              <LineProjectsCard
                detail={detail}
                editable={canFileInvoice(detail.status)}
                onSaved={setDetail}
              />
            )}
            {reviewTab === "weeks" &&
              (detail.validation ? (
                <MonthWeeksCard
                  weeks={detail.validation.weeks}
                  contractorId={detail.contractorId}
                  period={detail.period}
                />
              ) : (
                <p className="px-1 text-sm text-subtle">
                  The weeks appear once the document has been read.
                </p>
              ))}
            {reviewTab === "history" && <InvoiceHistory events={detail.events} />}
          </div>
        </div>
      </div>
    </div>
  );
}
